/******************************************************************************
 * MinimalStreamViewerImproved.tsx – “just-works” version
 *
 *  • Plays every Janus video stream
 *  • Subscribes to matching “BBox …” data streams
 *  • Paints green rectangles on top of the <video>
 *
 *****************************************************************************/

import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  useLayoutEffect
} from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Alert,
  useMediaQuery,
  Grid,
  Paper
} from '@mui/material';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { DashboardService } from '../../service/dashboard.service';



/* ------------------------------------------------------------------ */
/* GLOBALS (provided via <script> tags in index.html)                 */
/* ------------------------------------------------------------------ */
declare const Janus: any;


/* ------------------------------------------------------------------ */
/* CONSTANTS                                                          */
/* ------------------------------------------------------------------ */
const FALLBACK_W = 0;          // ← default if metadata never fires
const FALLBACK_H = 360;

/* ------------------------------------------------------------------ */
/* TYPES                                                              */
/* ------------------------------------------------------------------ */
interface Config {
  subdomain: string;
  turnIp: string;
}

interface StreamMeta {
  id: number;
  description: string;
}

type StreamStatus =
  | 'initial'
  | 'connecting'
  | 'started'
  | 'stopped'
  | 'error';

interface StreamState {
  meta: StreamMeta;
  status: StreamStatus;
  error?: string;
  remoteStream?: MediaStream;
  videoSize?: { w: number; h: number };
  boxes?: number[][];                // [ [x1,y1,x2,y2], … ]
  incident: boolean
}

interface State {
  loadingConfig: boolean;
  configError: string | null;
  config: Config | null;
  streams: Record<number, StreamState>;
  wsReady: boolean;
}

type Action =
  | { type: 'CONFIG_LOADING' }
  | { type: 'CONFIG_SUCCESS'; payload: Config }
  | { type: 'CONFIG_ERROR'; payload: string }
  | { type: 'STREAM_ADD'; payload: StreamMeta }
  | {
    type: 'STREAM_STATUS';
    payload: {
      id: number;
      status: StreamStatus;
      error?: string;
      remoteStream?: MediaStream;
    };
  }
  | { type: 'STREAM_REMOVE'; payload: { id: number } }
  | { type: 'SET_VIDEO_SIZE'; payload: { id: number; w: number; h: number } }
  | { type: 'UPDATE_BOXES'; payload: { camera: string; boxes: number[][] } }
  | { type: 'MARK_INCIDENT'; payload: { camera: string; incident: boolean } }
  | { type: 'WS_READY' }
  | { type: 'WS_CLOSE' };

/* ------------------------------------------------------------------ */
/* REDUCER                                                            */
/* ------------------------------------------------------------------ */
const initialState: State = {
  loadingConfig: true,
  configError: null,
  config: null,
  streams: {},
  wsReady: false,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'CONFIG_LOADING':
      return { ...state, loadingConfig: true, configError: null };

    case 'CONFIG_SUCCESS':
      return { ...state, loadingConfig: false, config: action.payload };

    case 'CONFIG_ERROR':
      return { ...state, loadingConfig: false, configError: action.payload };

    case 'STREAM_ADD':
      if (state.streams[action.payload.id]) {
        return state; // bereits vorhanden – kein Update
      }
      return {
        ...state,
        streams: {
          ...state.streams,
          [action.payload.id]: { meta: action.payload, status: 'initial', incident: false },
        },
      };

    case 'STREAM_STATUS':
      return {
        ...state,
        streams: {
          ...state.streams,
          [action.payload.id]: {
            ...state.streams[action.payload.id],
            status: action.payload.status,
            error: action.payload.error,
            remoteStream:
              action.payload.remoteStream ??
              state.streams[action.payload.id]?.remoteStream,
          },
        },
      };

    case 'STREAM_REMOVE': {
      const { [action.payload.id]: _gone, ...rest } = state.streams;
      return { ...state, streams: rest };
    }

    case 'SET_VIDEO_SIZE': {
      const { id, w, h } = action.payload;
      return {
        ...state,
        streams: {
          ...state.streams,
          [id]: { ...state.streams[id], videoSize: { w, h } },
        },
      };
    }

    case 'UPDATE_BOXES': {
      const { camera, boxes } = action.payload;

      /* 1️⃣ exact match first (case-insensitive, accent-insensitive) */
      let match = Object.values(state.streams).find(
        (s) =>
          !s.meta.description.startsWith('BBox') &&
          s.meta.description.localeCompare(camera, undefined, {
            sensitivity: 'accent',
          }) === 0,
      );

      /* 2️⃣ fuzzy fallback                                        */
      if (!match) {
        match = Object.values(state.streams).find(
          (s) =>
            !s.meta.description.startsWith('BBox') &&
            (s.meta.description.toLowerCase() === camera.toLowerCase() ||
              camera.toLowerCase().includes(s.meta.description.toLowerCase()) ||
              s.meta.description
                .toLowerCase()
                .includes(camera.toLowerCase())),
        );
      }

      if (!match) {
        console.warn(
          '[BBox] No matching video stream found for',
          camera,
          '; existing streams:',
          Object.values(state.streams)
            .filter((s) => !s.meta.description.startsWith('BBox'))
            .map((s) => s.meta.description),
        );
        return state;
      }

      const matchId = match.meta.id;
      const old = state.streams[matchId];
      return {
        ...state,
        streams: {
          ...state.streams,
          [matchId]: {
            ...old,
            boxes,
            videoSize: old.videoSize ?? { w: FALLBACK_W, h: FALLBACK_H },
          },
        },
      };
    }

    case 'WS_READY':
      return { ...state, wsReady: true };

    case 'WS_CLOSE':
      return { ...state, wsReady: false };
    case 'MARK_INCIDENT': {
      const { camera, incident } = action.payload;

      // Suche nach dem passenden Stream wie bei UPDATE_BOXES
      let match = Object.values(state.streams).find(
        (s) =>
          !s.meta.description.startsWith('BBox') &&
          s.meta.description.localeCompare(camera, undefined, {
            sensitivity: 'accent',
          }) === 0,
      );

      if (!match) {
        match = Object.values(state.streams).find(
          (s) =>
            !s.meta.description.startsWith('BBox') &&
            (s.meta.description.toLowerCase() === camera.toLowerCase() ||
              camera.toLowerCase().includes(s.meta.description.toLowerCase()) ||
              s.meta.description.toLowerCase().includes(camera.toLowerCase())),
        );
      }

      if (!match) {
        console.warn(
          '[Incident] No matching video stream found for',
          camera,
          '; existing streams:',
          Object.values(state.streams)
            .filter((s) => !s.meta.description.startsWith('BBox'))
            .map((s) => s.meta.description),
        );
        return state;
      }

      const matchId = match.meta.id;
      const old = state.streams[matchId];
      return {
        ...state,
        streams: {
          ...state.streams,
          [matchId]: {
            ...old,
            incident,
          },
        },
      };
    }
    default:
      return state;
  }
}

/* ------------------------------------------------------------------ */
/* COMPONENT                                                          */
/* ------------------------------------------------------------------ */
const MinimalStreamViewerImproved: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [cameraHeight, setCameraHeight] = useState(200);
  const [isCameraVisible, setIsCameraVisible] = useState(true);

  const isLargeScreen = useMediaQuery("(min-width:1535px)");



  /* ---- refs that outlive renders -------------------------------- */
  const janusRef = useRef<any>(null);
  const handlesRef = useRef<Record<number, any>>({});
  const wsRef = useRef<WebSocket | null>(null);
  const pcsRef = useRef<Record<string, RTCPeerConnection>>({});

  /* -------------------- helpers ---------------------------------- */
  const updateBoxes = useCallback(
    (camera: string, boxes?: number[][]) => {
      if (boxes && boxes.length > 0) {
        dispatch({ type: 'UPDATE_BOXES', payload: { camera, boxes } });
      } else {
        dispatch({ type: 'MARK_INCIDENT', payload: { camera, incident: true } });
      }
    },
    [],
  );

  /** Convert either *pixel* or *normalised* values to % */
  const asPerc = (value: number) =>
    value = value * 100;

  /* -------------------- Data-channel via external hub ------------- */
  const subscribeToDataStream = useCallback(
    async (streamId: string) => {
      if (!state.config) return;

      const token = localStorage.getItem('token');
      if (!token || pcsRef.current[streamId]) return;

      const { turnIp, subdomain } = state.config;
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          {
            urls: `turn:${turnIp}:3478`,
            username: 'myuser',
            credential: 'mypass',
          },
        ],
      });
      pcsRef.current[streamId] = pc;

      const dc = pc.createDataChannel('data');
      dc.onopen = () => console.log(`✅ DC open for ext #${streamId}`);
      dc.onmessage = (e: MessageEvent) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.camera && Array.isArray(msg.boxes))
            updateBoxes(msg.camera, msg.boxes);
        } catch {
          /* ignore JSON errors */
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (['closed', 'failed', 'disconnected'].includes(pc.iceConnectionState)) {
          pc.close();
          delete pcsRef.current[streamId];
        }
      };

      /* --- SDP round-trip to the backend ------------------------ */
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const resp = await fetch(
        `https://${subdomain}.washprotech.com:8080/client_subscribe`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            stream_id: streamId,
            sdp: pc.localDescription?.sdp,
            type: pc.localDescription?.type,
          }),
        },
      );
      if (!resp.ok) {
        pc.close();
        delete pcsRef.current[streamId];
        return;
      }

      const answer = await resp.json();
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    },
    [state.config, updateBoxes],
  );

  /* ------------------------- WebSocket --------------------------- */
  const initWebSocket = useCallback(() => {
    if (!state.config) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(
      `${proto}://${state.config.subdomain}.washprotech.com:8080/ws?token=${token}`,
    );
    wsRef.current = ws;

    ws.onopen = () => {
      dispatch({ type: 'WS_READY' });
      ws.send(JSON.stringify({ action: 'list_streams' }));
    };

    ws.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d.action === 'streams') d.streams.forEach(subscribeToDataStream);
        if (d.action === 'new_stream') subscribeToDataStream(d.stream_id);
      } catch { /* ignore */ }
    };

    ws.onclose = () => dispatch({ type: 'WS_CLOSE' });
  }, [state.config, subscribeToDataStream]);

  /* -------------------------- Janus ------------------------------ */
  const initJanus = useCallback(() => {
    if (!state.config) return;
    const { subdomain, turnIp } = state.config;

    Janus.init({
      debug: 'warn',
      callback: () => {
        if (!Janus.isWebrtcSupported()) {
          dispatch({ type: 'CONFIG_ERROR', payload: 'WebRTC unsupported' });
          return;
        }

        janusRef.current = new Janus({
          server: `https://${subdomain}.api.washprotech.com:8889/janus`,
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            {
              urls: `turn:${turnIp}:3478?transport=udp`,
              username: 'myuser',
              credential: 'mypass',
            },
          ],
          success: () => attachListHandle(),
          error: (e: any) =>
            dispatch({ type: 'CONFIG_ERROR', payload: String(e) }),
        });
      },
    });
  }, [state.config]);

  /* --------------------- Helpers for Janus ----------------------- */
  const attachListHandle = () => {
    const janus = janusRef.current;
    if (!janus) return;

    janus.attach({
      plugin: 'janus.plugin.streaming',
      opaqueId: `list-${Janus.randomString(8)}`,

      success: (h: any) => {
        h.send({
          message: { request: 'list' },
          success: (res: any) => {
            const newList = res.list as StreamMeta[];

            newList.forEach((m) => {
              // Bereits bekannte Streams NICHT erneut dispatchen
              const exists = state.streams[m.id]; // statt knownStreamsRef
              if (!exists) {
                console.warn(`[ListHandle] Neuer Stream ${m.id} erkannt – wird hinzugefügt`);
                dispatch({ type: 'STREAM_ADD', payload: m });
              }

              // knownStreamsRef aktuell halten
              if (!knownStreamsRef.current.some((s) => s.id === m.id)) {
                knownStreamsRef.current.push(m);
              }
            });

            h.detach();
          },
        });
      },
    });
  };

  const attachStream = useCallback(
    (stream: StreamMeta) => {
      const janus = janusRef.current;
      if (!janus) return;

      dispatch({
        type: 'STREAM_STATUS',
        payload: { id: stream.id, status: 'connecting' },
      });

      janus.attach({
        plugin: 'janus.plugin.streaming',
        opaqueId: `watch-${stream.id}-${Janus.randomString(8)}`,

        success: (h: any) => {
          handlesRef.current[stream.id] = h;
          h.send({ message: { request: 'watch', id: stream.id } });

          /* ---------- if this is a BBox data stream -------------- */
          if (stream.description.startsWith('BBox')) {
            const target = stream.description.match(/\[(.*)\]/)?.[1] ?? '';

            h.ondataopen = () =>
              console.log(`📡 helper DC open #${stream.id}`);

            h.ondata = (raw: string) => {
              try {
                const { boxes } = JSON.parse(raw);
                if (Array.isArray(boxes)) updateBoxes(target, boxes);
              } catch { /* ignore parse errors */ }
            };
          }
        },

        onmessage: (_msg: any, jsep: any) => {
          if (!jsep) return;

          handlesRef.current[stream.id].createAnswer({
            jsep,
            tracks: [{ type: 'data' }],
            success: (ans: any) =>
              handlesRef.current[stream.id].send({
                message: { request: 'start' },
                jsep: ans,
              }),
          });
        },

        onremotetrack: (
          track: MediaStreamTrack,
          _mid: string,
          on: boolean,
        ) => {
          if (!on || track.kind !== 'video') return;

          const media = new MediaStream([track]);

          /** width / height as offered in the SDP (may change later) */
          const stgs = track.getSettings();
          if (stgs.width && stgs.height)
            dispatch({
              type: 'SET_VIDEO_SIZE',
              payload: { id: stream.id, w: stgs.width, h: stgs.height },
            });

          dispatch({
            type: 'STREAM_STATUS',
            payload: {
              id: stream.id,
              status: 'started',
              remoteStream: media,
            },
          });
        },

        oncleanup: () =>
          dispatch({ type: 'STREAM_REMOVE', payload: { id: stream.id } }),
      });
    },
    [updateBoxes],
  );

  /* ---------------------------- effects -------------------------- */
  /* 1) load backend config ---------------------------------------- */
  useEffect(() => {
    dispatch({ type: 'CONFIG_LOADING' });

    DashboardService.getDashboard()
      .then((d) => {
        if (!d.subdomain || !d.turnIp) throw new Error('Bad cfg');
        dispatch({
          type: 'CONFIG_SUCCESS',
          payload: { subdomain: d.subdomain, turnIp: d.turnIp },
        });
      })
      .catch((e: Error) =>
        dispatch({ type: 'CONFIG_ERROR', payload: e.message }),
      );
  }, []);

  /* 2) once config is present, start Janus + WebSocket ------------ */
  useEffect(() => {
    if (state.config && !state.configError) {
      if (!janusRef.current) initJanus();
      if (!wsRef.current) initWebSocket();
    }
  }, [state.config, state.configError, initJanus, initWebSocket]);

  /* 3) whenever new stream meta is added, attach ------------------ */
  useEffect(() => {
    Object.values(state.streams).forEach(
      (s) => s.status === 'initial' && attachStream(s.meta),
    );
  }, [state.streams, attachStream]);

  /* 4) mount MediaStream -> <video> and capture **displayed** size */
  useEffect(() => {
    Object.values(state.streams).forEach((s) => {
      if (!s.remoteStream) return;

      const v = document.getElementById(
        `video-${s.meta.id}`,
      ) as HTMLVideoElement | null;

      if (v && v.srcObject !== s.remoteStream) {
        v.srcObject = s.remoteStream;

        v.onloadedmetadata = () => {
          const rect = v.getBoundingClientRect();
          dispatch({
            type: 'SET_VIDEO_SIZE',
            payload: {
              id: s.meta.id,
              w: rect.width,
              h: rect.height,
            },
          });
        };
      }
    });
  }, [state.streams]);

  /* 5) global cleanup -------------------------------------------- */
  useEffect(
    () => () => {
      Object.values(handlesRef.current).forEach((h) => h.detach?.());
      janusRef.current?.destroy?.();
      wsRef.current?.close();
      Object.values(pcsRef.current).forEach((pc) => pc.close());
    },
    [],
  );

  const knownStreamsRef = useRef<StreamMeta[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      attachListHandle();
    }, 30000); // alle 30 Sekunden die Liste neu abfragen
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentIds = Object.keys(state.streams).map((id) => Number(id));

      knownStreamsRef.current.forEach((knownStream) => {
        if (!currentIds.includes(knownStream.id)) {
          console.warn(`[Reconnect] Stream ${knownStream.id} fehlt – reconnect wird ausgelöst`);
          dispatch({ type: 'STREAM_ADD', payload: knownStream });
          attachStream(knownStream);
        }
      });
    }, 15000); // alle 15 Sekunden prüfen

    return () => clearInterval(interval);
  }, [state.streams, attachStream]);


  //Setzen der Queue
  const [incidentQueue, setIncidentQueue] = useState<StreamState[]>([]);
  const [bigStream, setBigStream] = useState<StreamState | null>(null);
  const [videoScales, setVideoScales] = useState<Record<number, { scaleX: number; scaleY: number }>>({});

  const updateScales = () => {
    Object.values(state.streams).forEach((stream) => {
      const videoEl = document.getElementById(`video-${stream.meta.id}`) as HTMLVideoElement | null;
      if (!videoEl || !stream.videoSize) return;

      const renderedW = videoEl.clientWidth;
      const renderedH = videoEl.clientHeight;

      const videoW = stream.videoSize.w;
      const videoH = stream.videoSize.h;

      const scaleX = renderedW / videoW;
      const scaleY = renderedH / videoH;

      setVideoScales((prev) => ({
        ...prev,
        [stream.meta.id]: { scaleX, scaleY },
      }));
    });
  };

  useLayoutEffect(() => {
    if (!bigStream) return;
    updateScales(); // zentrale Methode ruft alles ab
  }, [bigStream]);

  useEffect(() => {
    const streams = Object.values(state.streams);

    const newQueue = streams.filter(s => s.incident);

    setIncidentQueue(prevQueue => {
      const idsInQueue = new Set(prevQueue.map(s => s.meta.id));
      const updatedQueue = [...prevQueue];

      // Neue Incidents hinzufügen
      newQueue.forEach(s => {
        if (!idsInQueue.has(s.meta.id)) {
          updatedQueue.push(s);
        }
      });

      // Incidents entfernen, die nicht mehr aktuell sind
      return updatedQueue.filter(s => newQueue.find(n => n.meta.id === s.meta.id));
    });
  }, [state.streams]);

  // Großen Stream setzen auf Basis ob ein Incident passiert ist
  useEffect(() => {
    if (incidentQueue.length > 0) {
      setBigStream(incidentQueue[0]);
    } else {
      setBigStream(null); // Kein Incident → kein großer Stream
    }
  }, [incidentQueue]);

  //Bildschirm Kachelanpassung
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight;
      const headerHeight = 20; // Höhe Header
      const spacing = 60; // Abstand unten

      let descriptionCount = 0;

      const filteredStreams = Object.values(state.streams)
        .filter((s) => {
          const hasDescription = s.meta?.description;
          if (hasDescription && !hasDescription.startsWith("BBox")) {
            descriptionCount++;
            return true;
          }
          return false;
        });

      const numGridRows = Math.ceil(descriptionCount / 9); // Max. 9 Kacheln pro Zeile

      // Verteile den verfügbaren Platz dynamisch je nach Anzahl der Zeilen
      let cameraRatio = 0.55; // Default: große Kamera
      if (numGridRows > 1) cameraRatio = 0.35; // Kleinere Kamera bei zwei Zeilen

      const availableHeight = windowHeight - headerHeight - spacing;
      const newCameraHeight = Math.max(130, availableHeight * cameraRatio);

      setCameraHeight(newCameraHeight);
      setIsCameraVisible(newCameraHeight > 150);
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };

  }, [state.streams]);

  useEffect(() => {
    updateScales(); // initial

    window.addEventListener("resize", updateScales);
    return () => window.removeEventListener("resize", updateScales);
  }, [state.streams]);



  const cameraWidth = cameraHeight * (16 / 9);
  const s = bigStream;
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const HEIGHT = isSmallScreen ? 50 : 40;
  const aspect = "16 / 9";



  return (
    <Container
      maxWidth="xl"
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        padding: 2,
        overflowY: isLargeScreen ? "hidden" : "auto",
      }}
    >
      {/* Header */}
      <Typography variant="h4" sx={{ marginBottom: 3 }}>
        Washprotech {knownStreamsRef.current.length} {Object.values(state.streams).length}
      </Typography>


      {/* Hauptinhalt */}
      {/* Großer Stream */}
      {bigStream ? (
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          {/* Große Kameraanzeige nur auf großen Bildschirmen */}
          {/*} <Typography>{cameraHeight} {window.innerWidth} {Object.values(state.streams).length} {Math.ceil(Object.values(state.streams).slice(0,18).length / 9)} {Math.max(220, Math.ceil(Object.values(state.streams).slice(0,18).length / 9) * 160)}</Typography> */}
          {isLargeScreen && isCameraVisible && (
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: cameraHeight,
                minHeight: "150px",
                maxHeight: "calc(100vh - 350px)",
              }}
            >
              <Box sx={{ width: `${cameraWidth}px`, height: `${cameraHeight}px` }}>
                <Box sx={{ position: 'relative', width: '100%', marginTop: 1 }}>

                  {/*Obere Box für Incidents */}
                  {bigStream.incident && (


                    <Box sx={{ height: HEIGHT }}>
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'red',
                          color: 'white',
                        }}
                      >
                        <WarningAmber sx={{ marginRight: 1 }} />
                        <Typography fontWeight="bold" fontSize={isSmallScreen ? 16 : 14}>
                          Situation prüfen
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Kamera-Card mit Bild */}
                  {/*videoScales[s.meta.id] &&(
        <Box>
              <Typography> {videoScales[s.meta.id].scaleY} {document.getElementById("videoBox")?.clientWidth}</Typography>
                <Paper elevation={2} sx={{ padding: 2, backgroundColor: "#f5f5f5" }}>
                  <pre>{JSON.stringify(s, null, 2)}</pre>
                </Paper>
        </Box>
      )*/}
                  <Box
                    sx={{
                      backgroundColor: bigStream.incident ? 'red' : 'green',
                      color: 'white',
                      textAlign: 'center',
                      borderRadius: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      position: 'relative',
                    }}
                  >
                    <Box sx={{
                      width: "100%",
                      aspectRatio: aspect, // sorgt für konstanten Platz
                      backgroundColor: "#e0e0e0", // neutraler Hintergrund für Ladezeit
                      position: "relative",
                      overflow: "hidden",
                    }}>
                      {bigStream.status === "started" ? (
                        <video
                          id={`video-${bigStream.meta.id}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          playsInline
                          muted
                          autoPlay
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            aspectRatio: aspect,
                            backgroundColor: "#e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                          }}
                        >
                          <CircularProgress size={25} />
                          <Typography sx={{ marginTop: 2 }} variant="body2">
                            {bigStream.status === "connecting" ? "Verbindung wird aufgebaut…" : "Kein Stream verfügbar"}
                          </Typography>
                        </Box>
                      )}


                      {/* Boxen für erkannte Autos */}
                      {bigStream.status === "started" && !!bigStream.boxes?.length && (
                        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                          {bigStream.boxes.map(([x1, y1, x2, y2], i) => (
                            <Box
                              key={i}
                              sx={{
                                position: "absolute",
                                border: "2px solid #0f0",
                                left: `${asPerc(x1)}%`,
                                top: `${asPerc(y1)}%`,
                                width: `${asPerc(x2 - x1 - 0.01)}%`,
                                height: `${asPerc(y2 - y1)}%`,
                                transition: "all .1s linear",
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>

                    <Box
                      sx={{
                        height: HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'green',
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        fontSize={isSmallScreen ? 16 : 14}
                      >
                        {bigStream.meta.description}
                      </Typography>

                    </Box>
                  </Box>
                </Box>
              </Box>

            </Box>
          )}


          {/*{
  "meta": {
    "id": 2,
    "type": "live",
    "description": "D4 RUT",
    "enabled": true,
    "media": [
      {
        "mid": "v",
        "type": "video",
        "label": "RTSP video",
        "age_ms": 4
      }
    ]
  },
  "status": "started",
  "incident": false,
  "remoteStream": {},
  "videoSize": {
    "w": 684.4500122070312,
    "h": 385
  },
  "boxes": [
    [
      0,
      73,
      8,
      114
    ]
  ]
}} */}







          {/* Bereich kleine Kameras */}
          <Box
            sx={{
              flexShrink: 0,
              paddingBottom: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              //flexGrow: 1,
            }}
          >
            <Grid container spacing={2} justifyContent="flex-start">
              {Object.values(state.streams)
                .filter((s) => !s.meta.description.startsWith("BBox"))
                .slice(0, 12)
                .map((s) => {
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={1.33} >
                      <Box sx={{ position: 'relative', width: '100%', marginTop: 1, mt: 1, mb: 2 }}>
                        {s.incident && (
                          <Box sx={{ height: HEIGHT }}>
                            <Box
                              sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'red',
                                color: 'white',
                              }}
                            >
                              <WarningAmber sx={{ marginRight: 1 }} />
                              <Typography fontWeight="bold" fontSize={isSmallScreen ? 16 : 14}>
                                Situation prüfen
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Kamera-Card mit Bild */}
                        <Box
                          sx={{
                            backgroundColor: 'green',
                            color: 'white',
                            textAlign: 'center',
                            borderRadius: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            position: 'relative'
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              aspectRatio: "16 / 9", // Einheitliche Höhe für alle Videos
                              backgroundColor: "#e0e0e0",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            {s.status === "started" ? (
                              <video
                                id={`video-${s.meta.id}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                                playsInline
                                muted
                                autoPlay
                              />
                            ) : (
                              <Box
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                  padding: 1,
                                }}
                              >
                                <CircularProgress size={10} />
                                <Typography
                                  variant="caption"
                                  sx={{ marginTop: 1, textAlign: "center" }}
                                >
                                  {s.status === "connecting"
                                    ? "Verbindung wird aufgebaut…"
                                    : "Stream nicht verfügbar"}
                                </Typography>
                              </Box>
                            )}

                            {/* Boxen für erkannte Autos */}
                            {s.status === "started" && !!s.boxes?.length && (
                              <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                                {s.boxes.map(([x1, y1, x2, y2], i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      position: "absolute",
                                      border: "2px solid #0f0",
                                      left: `${asPerc(x1)}%`,
                                      top: `${asPerc(y1)}%`,
                                      width: `${asPerc(x2 - x1 - 0.01)}%`,
                                      height: `${asPerc(y2 - y1)}%`,
                                      transition: "all .1s linear",
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>

                          <Box
                            sx={{
                              height: HEIGHT,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'green',
                            }}
                          >

                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              fontSize={isSmallScreen ? 16 : 14}
                            >
                              {s.meta.description}
                            </Typography>

                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  )
                })}
            </Grid>
          </Box>
        </Box>

      ) : (
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            paddingRight: 1,
          }}
        >
          {/* Falls kein Incident eingetroffen ist */}
          {/*}
          <Paper elevation={2} sx={{ padding: 2, backgroundColor: "#f5f5f5" }}>
            <pre>{JSON.stringify(state.streams, null, 2)}</pre>
          </Paper> */}
          <Grid container spacing={2}>
            {Object.values(state.streams)
              .filter((s) => !s.meta.description.startsWith("BBox"))
              .map((s) => (
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Box sx={{ position: 'relative', width: '100%', marginTop: 1, mt: 1, mb: 2 }}>
                    {s.incident && (
                      <Box sx={{ height: HEIGHT }}>
                        <Box
                          sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'red',
                            color: 'white',
                          }}
                        >

                          <WarningAmber sx={{ marginRight: 1 }} />
                          <Typography fontWeight="bold" fontSize={isSmallScreen ? 16 : 14}>
                            Situation prüfen
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Kamera-Card mit Bild */}
                    <Box
                      sx={{
                        backgroundColor: 'green',
                        color: 'white',
                        textAlign: 'center',
                        borderRadius: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          aspectRatio: "16 / 9", // Einheitliche Höhe für alle Videos
                          backgroundColor: "#e0e0e0",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        {s.status === "started" ? (
                          <video
                            id={`video-${s.meta.id}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            playsInline
                            muted
                            autoPlay
                          />
                        ) : (
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "text.secondary",
                              padding: 1,
                            }}
                          >
                            <CircularProgress size={10} />
                            <Typography
                              variant="caption"
                              sx={{ marginTop: 1, textAlign: "center" }}
                            >
                              {s.status === "connecting"
                                ? "Verbindung wird aufgebaut…"
                                : "Stream nicht verfügbar"}
                            </Typography>
                          </Box>
                        )}

                        {/* Boxen für erkannte Autos */}
                        {s.status === "started" && !!s.boxes?.length && (
                          <Box sx={{
                            position: "absolute",
                            inset: 0,
                            pointerEvents: "none",
                            opacity: s.boxes?.length ? 1 : 0,
                            transition: "opacity 0.2s linear",
                          }}>
                            {s.boxes.map(([x1, y1, x2, y2], i) => (
                              <Box
                                key={i}
                                sx={{
                                  position: "absolute",
                                  border: "2px solid #0f0",
                                  left: `${asPerc(x1)}%`,
                                  top: `${asPerc(y1)}%`,
                                  width: `${asPerc(x2 - x1 - 0.01)}%`,
                                  height: `${asPerc(y2 - y1)}%`,
                                  transition: "all .1s linear",
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>

                      <Box
                        sx={{
                          height: HEIGHT,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'green',
                        }}
                      >
                        {/*}
                        <Paper elevation={2} sx={{ padding: 2, backgroundColor: "#f5f5f5" }}>
                          <pre>{JSON.stringify(s, null, 2)}</pre>
                        </Paper> */}
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          fontSize={isSmallScreen ? 16 : 14}
                        >
                          {s.meta.description} {s.status}
                        </Typography>

                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
          </Grid>
        </Box>)}
    </Container>
  );

  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      <Typography variant="h4" sx={{ p: 2 }}>
        Minimal&nbsp;Multi-Stream&nbsp;Viewer
      </Typography>

      {/* Großer Stream */}
      {state.streams && Object.values(state.streams).length > 0 && (() => {
        const s = Object.values(state.streams)[0];

        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              px: 2,
              pt: 2,
              flexGrow: 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 800,
                aspectRatio: "16 / 9",
                position: "relative",
                bgcolor: s.incident ? "red" : "green",
                color: "white",
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                flexShrink: 1,
              }}
            >
              {s.incident && (
                <Box sx={{ height: 40, bgcolor: "red", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography fontWeight="bold" fontSize={14}>Situation prüfen</Typography>
                </Box>
              )}

              <Box sx={{ position: "relative", flexGrow: 1 }}>
                <video
                  id={`video-${s.meta.id}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: s.status === "started" ? "block" : "none",
                  }}
                  playsInline
                  muted
                  autoPlay
                />

                {s.status === "started" && !!s.boxes?.length && (
                  <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                    {/*s.boxes.map(([x1, y1, x2, y2], i) => (
                    <Box
                      key={i}
                      sx={{
                        position: "absolute",
                        border: "2px solid #0f0",
                        left: `${asPerc(x1, FALLBACK_W)}%`,
                        top: `${asPerc(y1, FALLBACK_H)}%`,
                        width: `${asPerc(x2 - x1, FALLBACK_W)}%`,
                        height: `${asPerc(y2 - y1, FALLBACK_H)}%`,
                        transition: "all .1s linear",
                      }}
                    />
                  ))*/}
                  </Box>
                )}

                {s.status !== "started" && s.status !== "error" && (
                  <Typography
                    variant="body2"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "rgba(0,0,0,0.3)",
                      px: 1,
                      borderRadius: 1,
                    }}
                  >
                    {s.status === "connecting"
                      ? "Connecting…"
                      : s.status === "stopped"
                        ? "Stream stopped"
                        : "Pending…"}
                  </Typography>
                )}
              </Box>

              <Box sx={{ height: 40, bgcolor: "green", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography fontWeight="bold" fontSize={16}>{s.meta.description} {s.status}</Typography>
              </Box>
            </Box>
          </Box>
        );
      })()}

      {/* Kleine Streams */}
      <Box sx={{ flex: 1, p: 2, overflow: "hidden" }}>
        <Grid container spacing={2} sx={{ height: "100%", overflow: "hidden" }}>
          {Object.values(state.streams)
            .filter((s) => !s.meta.description.startsWith("BBox"))
            .slice(0, 12)
            .map((s) => {
              const w = s.videoSize?.w ?? FALLBACK_W;
              const h = s.videoSize?.h ?? FALLBACK_H;

              return (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={2} key={s.meta.id}>
                  <Box
                    sx={{
                      backgroundColor: s.incident ? "red" : "green",
                      color: "white",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    {s.incident && (
                      <Box sx={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "red" }}>
                        <Typography fontWeight="bold" fontSize={14}>Situation prüfen</Typography>
                      </Box>
                    )}

                    <Box sx={{ position: "relative", flexGrow: 1 }}>
                      <video
                        id={`video-${s.meta.id}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: s.status === "started" ? "block" : "none",
                        }}
                        playsInline
                        muted
                        autoPlay
                      />

                      {s.status === "started" && !!s.boxes?.length && (
                        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                          {s.boxes.map(([x1, y1, x2, y2], i) => (
                            <Box
                              key={i}
                              sx={{
                                position: "absolute",
                                border: "2px solid #0f0",
                                //left: `${asPerc(x1, w)}%`,
                                // top: `${asPerc(y1, h)}%`,
                                //width: `${asPerc(x2 - x1, w)}%`,
                                // height: `${asPerc(y2 - y1, h)}%`,
                                transition: "all .1s linear",
                              }}
                            />
                          ))}
                        </Box>
                      )}

                      {s.status !== "started" && s.status !== "error" && (
                        <Typography
                          variant="body2"
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            bgcolor: "rgba(0,0,0,0.3)",
                            px: 1,
                            borderRadius: 1,
                          }}
                        >
                          {s.status === "connecting"
                            ? "Connecting…"
                            : s.status === "stopped"
                              ? "Stream stopped"
                              : "Pending…"}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "green" }}>
                      <Typography fontWeight="bold" fontSize={16}>{s.meta.description} {s.status}</Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
        </Grid>
      </Box>
    </Container>
  );



};

export default MinimalStreamViewerImproved;
